import { useCallback, useEffect, useRef, useState } from 'react';
import type React from 'react';

/**
 * Pins an element to fullscreen while a handheld device is held in landscape,
 * releasing the browser chrome so a wide surface (e.g. the whiteboard canvas)
 * can fill the screen. Exits fullscreen on rotate back to portrait.
 */
export function useLandscapeFullscreen<T extends HTMLElement>(containerRef: React.RefObject<T | null>) {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const fsRequestedRef = useRef(false);

  const enter = useCallback(() => {
    const el = containerRef.current;
    if (!el || fsRequestedRef.current) return;
    fsRequestedRef.current = true;
    try {
      const anyEl = el as T & { webkitRequestFullscreen?: () => void };
      const result = el.requestFullscreen ? el.requestFullscreen() : anyEl.webkitRequestFullscreen?.();
      void (result as Promise<void> | undefined)?.catch(() => {
        // Fullscreen requires a prior user gesture on some browsers — retry on
        // the next orientationchange or let the user toggle it manually.
        fsRequestedRef.current = false;
      });
    } catch {
      fsRequestedRef.current = false;
    }
  }, [containerRef]);

  const exit = useCallback(() => {
    const doc = document as Document & {
      webkitExitFullscreen?: () => void;
      webkitFullscreenElement?: Element | null;
    };
    if (doc.fullscreenElement || doc.webkitFullscreenElement) {
      doc.exitFullscreen?.() ?? doc.webkitExitFullscreen?.();
    }
  }, []);

  useEffect(() => {
    const onFsChange = () => {
      const doc = document as Document & { webkitFullscreenElement?: Element | null };
      const active = Boolean(doc.fullscreenElement || doc.webkitFullscreenElement);
      setIsFullscreen(active);
      if (!active) fsRequestedRef.current = false;
    };

    const isHandheld = window.matchMedia('(pointer: coarse)').matches;
    const onOrientation = () => {
      if (!isHandheld) return;
      const angle = (screen.orientation?.angle ?? 0) % 180;
      const landscape = angle !== 0;
      const doc = document as Document & { webkitFullscreenElement?: Element | null };
      const fsActive = Boolean(doc.fullscreenElement || doc.webkitFullscreenElement);
      if (landscape && !fsActive) enter();
      if (!landscape && fsActive) exit();
    };

    document.addEventListener('fullscreenchange', onFsChange);
    document.addEventListener('webkitfullscreenchange', onFsChange as EventListener);
    screen.orientation?.addEventListener('change', onOrientation);
    window.addEventListener('orientationchange', onOrientation);

    return () => {
      document.removeEventListener('fullscreenchange', onFsChange);
      document.removeEventListener('webkitfullscreenchange', onFsChange as EventListener);
      screen.orientation?.removeEventListener('change', onOrientation);
      window.removeEventListener('orientationchange', onOrientation);
      if (fsRequestedRef.current) exit();
    };
  }, [containerRef, enter, exit]);

  return { isFullscreen, enter, exit };
}
